import { useCallback, useEffect, useRef, useState } from 'react';
import { EMPTY_PROGRESS, type OnboardingProgress, type UserPreferences } from './types';

const ANON_KEY = 'flow-weaver:onboarding-v1';
const userKey = (userId: string | null) =>
  userId ? `flow-weaver:onboarding-v1:${userId}` : ANON_KEY;
const DEBOUNCE_MS = 500;

function readLocal(key: string): OnboardingProgress {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { ...EMPTY_PROGRESS };
    const parsed = JSON.parse(raw) as Partial<OnboardingProgress>;
    return { ...EMPTY_PROGRESS, ...parsed };
  } catch {
    return { ...EMPTY_PROGRESS };
  }
}

function writeLocal(key: string, p: OnboardingProgress) {
  try { localStorage.setItem(key, JSON.stringify(p)); } catch { /* ignored */ }
}

/**
 * Migrate anonymous onboarding progress to a user-scoped key the first
 * time a user signs in. Prevents wizard / tour preferences collected
 * pre-login from being lost — and from colliding with those of other
 * users on the same device.
 */
function migrateAnonToUser(userId: string) {
  try {
    const target = userKey(userId);
    if (localStorage.getItem(target)) return; // already migrated or user had prior state
    const anon = localStorage.getItem(ANON_KEY);
    if (!anon) return;
    localStorage.setItem(target, anon);
    localStorage.removeItem(ANON_KEY);
  } catch { /* ignored */ }
}

export function useOnboardingProgress(userId: string | null) {
  const key = userKey(userId);
  const [progress, setProgress] = useState<OnboardingProgress>(() => readLocal(key));
  const [hydrated, setHydrated] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRef = useRef<OnboardingProgress>(progress);
  latestRef.current = progress;

  // On sign-in: move anonymous progress into this user's namespace so
  // wizard completion etc. survives the transition.
  useEffect(() => {
    if (userId) migrateAnonToUser(userId);
    setProgress(readLocal(userKey(userId)));
    setHydrated(true);
  }, [userId]);

  const flush = useCallback(() => {
    // Debounced no-op placeholder; future server sync can hook in here.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { /* persist hook */ }, DEBOUNCE_MS);
  }, []);


  const update = useCallback((patch: Partial<OnboardingProgress>) => {
    setProgress(prev => {
      const merged: OnboardingProgress = {
        ...prev,
        ...patch,
        updated_at: new Date().toISOString(),
      };
      writeLocal(key, merged);
      return merged;
    });
    flush();
  }, [flush, key]);

  const updatePreferences = useCallback((patch: Partial<UserPreferences>) => {
    setProgress(prev => {
      const next: OnboardingProgress = {
        ...prev,
        preferences: prev.preferences
          ? { ...prev.preferences, ...patch }
          : (patch as UserPreferences),
        updated_at: new Date().toISOString(),
      };
      writeLocal(key, next);
      return next;
    });
    flush();
  }, [flush, key]);

  const reset = useCallback(() => {
    const blank: OnboardingProgress = {
      ...EMPTY_PROGRESS,
      started_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    setProgress(blank);
    writeLocal(key, blank);
    flush();
  }, [flush, key]);

  return { progress, hydrated, update, updatePreferences, reset };
}
